import { useState } from 'react'
import type { FormEvent } from 'react'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ErrorNotice } from '@/components/ErrorNotice'
import { Checkbox, TextAreaField, TextField } from '@/components/Form'
import { SkeletonRows } from '@/components/Skeleton'
import { useAdminUsers, useAnnounce } from '@/hooks/useAdmin'
import { useStatus } from '@/hooks/useStatus'

export function AdminAnnouncePage() {
  const users = useAdminUsers()
  const announce = useAnnounce()
  const { isDegraded } = useStatus()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('')
  const [everyone, setEveryone] = useState(true)
  const [selected, setSelected] = useState<string[]>([])

  const items = users.data?.items ?? []
  const canSubmit =
    title.trim().length > 0 && body.trim().length > 0 && (everyone || selected.length > 0)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    announce.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || null,
        userIds: everyone ? null : selected,
      },
      {
        onSuccess: () => {
          setTitle('')
          setBody('')
          setUrl('')
        },
      },
    )
  }

  return (
    <div className="space-y-5 lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-6 lg:space-y-0">
      <Card>
        <CardHeader
          title="Announce"
          description="Sends an in-app notification and a push to everyone who has notifications on."
        />
        <CardBody className="space-y-4">
          {/*
            The reason this page exists: announcements are sent from Cloudflare,
            so they are the one channel that still works when the home server is
            unreachable. That has to be obvious *while* it is unreachable.
          */}
          <div
            className={`rounded-[3px] border px-3 py-2.5 ${
              isDegraded ? 'border-warn/40 bg-warn-dim' : 'border-line bg-elevated'
            }`}
          >
            <p className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={`size-1.5 rounded-full ${isDegraded ? 'bg-warn' : 'bg-ok'}`}
              />
              <span className={`signage text-[9px] ${isDegraded ? 'text-warn' : 'text-faint'}`}>
                {isDegraded ? 'Home server down · this still sends' : 'Sent from Cloudflare'}
              </span>
            </p>
            <p className="mt-1.5 text-sm text-muted">
              {isDegraded
                ? 'The home server is unreachable right now, and announcements do not go through it. This is the way to tell people what is happening.'
                : 'Announcements never touch the home server, so they go out during an outage too.'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <TextField
              label="Title"
              required
              maxLength={80}
              placeholder="Server maintenance tonight"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
            <TextAreaField
              label="Message"
              required
              rows={4}
              placeholder="Jellyfin will be offline from about 9pm for an hour while a disk is replaced."
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
            <TextField
              label="Link"
              type="url"
              placeholder="https://… (optional)"
              hint="Tapping the notification opens this."
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              loading={announce.isPending}
              disabled={!canSubmit}
            >
              {everyone ? 'Send to everyone' : `Send to ${selected.length} selected`}
            </Button>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Audience"
          action={
            !everyone ? (
              <span className="flex items-baseline gap-1.5">
                <span className="font-mono text-xs text-ink">{selected.length}</span>
                <span className="signage text-[9px] text-faint">picked</span>
              </span>
            ) : null
          }
        />
        <CardBody className="space-y-3">
          <Checkbox
            label="Everyone with an account"
            checked={everyone}
            onChange={(next) => setEveryone(next)}
          />
          {!everyone ? (
            users.isPending ? (
              <SkeletonRows rows={5} />
            ) : users.isError ? (
              <ErrorNotice error={users.error} onRetry={() => void users.refetch()} />
            ) : (
              <div className="max-h-96 space-y-2 overflow-y-auto rounded-[3px] border border-line bg-canvas p-3">
                {items.map((user) => (
                  <Checkbox
                    key={user.id}
                    label={<span className="font-mono">{user.username}</span>}
                    checked={selected.includes(user.id)}
                    onChange={(next) =>
                      setSelected((current) =>
                        next ? [...current, user.id] : current.filter((id) => id !== user.id),
                      )
                    }
                  />
                ))}
              </div>
            )
          ) : (
            <p className="text-sm text-muted">
              Everyone who has not muted announcements will get this.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
